import type { StyleSpecification } from 'maplibre-gl';

/**
 * Map styles built on Kartverket's public raster tiles.
 *
 * Replaces Google Maps, which required an API key and a billing account — and
 * whose key was hardcoded into the committed TypeScript. Kartverket's cache is
 * open, needs no key, and has better Norwegian coverage. Attribution is required
 * by their terms of use.
 *
 * @see https://kartverket.no/api-og-data/kartgrunnlag
 */

const ATTRIBUTION = '<a href="https://kartverket.no/" target="_blank" rel="noopener">Kartverket</a>';

/** Kartverket layer names available from the open cache. */
export type BaseLayer = 'topo' | 'greyscale';

const TILE_LAYERS: Record<BaseLayer, string> = {
  topo: 'topo',
  // The 2.2 map offered a custom "std_grayscale" Google style and satellite.
  // Kartverket has no open satellite layer, so greyscale topography stands in
  // for the styled view and the satellite option is dropped.
  greyscale: 'topograatone',
};

export function createStyle(layer: BaseLayer): StyleSpecification {
  return {
    version: 8,
    sources: {
      kartverket: {
        type: 'raster',
        tiles: [
          `https://cache.kartverket.no/v1/wmts/1.0.0/${TILE_LAYERS[layer]}/default/webmercator/{z}/{y}/{x}.png`,
        ],
        tileSize: 256,
        attribution: ATTRIBUTION,
        maxzoom: 18,
      },
    },
    layers: [
      {
        id: 'kartverket-raster',
        type: 'raster',
        source: 'kartverket',
      },
    ],
  };
}

/** Haslum, Bærum — the office, and the map's default centre. */
export const DEFAULT_CENTRE: [number, number] = [10.5555, 59.9195];

export const DEFAULT_ZOOM = 10;

/** Zoom applied when focusing a single company. */
export const COMPANY_ZOOM = 14;
